/**
 * The soft warnings one line item shows — the advisory nudges, as opposed to the
 * hard errors in `lineItemsErrorMessages` that stop a customer booking.
 *
 * Gathered here because they don't share a shape: one reads the description
 * against the locations, the other needs all three axes and the weight
 * together. The container wants the answers, not the two different ways of
 * arriving at them.
 */

import type { ErrorMessageType } from "@freightsimple/ui-core/FormLayout/FormItem";
import type { AddLineItemType } from "@/Shared/Types/lineItemTypes";
import type { AddLocationLocationType } from "@/Shared/Types/locationTypes";
import type { LineItemErrors } from "../Validators/lineItemsErrorMessages";
import { lineItemsDensityWarning } from "../Validators/lineItemsDensityWarning";
import { lineItemsGenerateWarningMessageForDescription } from "../Validators/lineItemsGenerateWarningMessageForDescription";
import { useLineItemsShipmentLimits } from "../Validators/useLineItemsShipmentLimits";

interface LineItemsWarnings {
  descriptionWarning: ErrorMessageType;
  /** The description field swaps its error styling for warning styling. */
  showDescriptionAsWarning: boolean;
  densityWarning: ErrorMessageType;
}

export function useLineItemsWarnings(
  lineItem: AddLineItemType,
  lineItemErrorMessages: LineItemErrors,
  pickupLocation: AddLocationLocationType,
  deliveryLocation: AddLocationLocationType,
): LineItemsWarnings {
  const limits = useLineItemsShipmentLimits();

  const descriptionWarning = lineItemsGenerateWarningMessageForDescription(
    lineItem.description || "",
    pickupLocation,
    deliveryLocation,
  );

  // Held back while a dimension or the weight is already in error: the customer
  // is being told to fix that field, and a density worked out from a number we
  // have just rejected would only argue with it.
  const dimensionsAreClean =
    lineItemErrorMessages.length === undefined &&
    lineItemErrorMessages.width === undefined &&
    lineItemErrorMessages.height === undefined &&
    lineItemErrorMessages.weight === undefined;

  return {
    descriptionWarning,
    showDescriptionAsWarning:
      lineItemErrorMessages.description === undefined &&
      descriptionWarning !== undefined,
    densityWarning: dimensionsAreClean
      ? lineItemsDensityWarning(lineItem, limits)
      : undefined,
  };
}
